import { useCallback, useMemo, useState, type FC } from 'react'
import { ISO8601Duration, Timescale } from '@opallabs/concepts'
import { MultiInput, type MultiInputProps } from '../MultiInput'
import {
  buildRepresentation,
  parseRepresentation,
  unitOptions,
  validatedUnit,
} from './data'
import { type DurationControlProps } from './types'

export { type DurationControlProps }
export const durationControlSelectors = {
  container: 'satellite-duration',
} as const

/**
 * Simple widget for setting a duration. Supports a subset of ISO 8601-1’s duration components.
 *
 * @note The dropdown text is localized, allowing it to automatically adjust pluralization based on the value.
 */
export const DurationControl: FC<DurationControlProps> = ({
  defaultTimescale: defaultUnit = Timescale.Day,
  duration,
  hideFieldsetLegend = false,
  htmlId,
  maxValue,
  minValue,
  omitFieldsetName = false,
  onChange,
  required,
}) => {
  const [initialValue, initialUnit] = parseRepresentation(duration)
  const [value, setValue] = useState(initialValue)

  const wrappedOnChange = useCallback<
    MultiInputProps<ISO8601Duration.TimeElement>['onChange']
  >(
    (data) => {
      if (!data) {
        setValue(undefined)
        onChange(null)
        return
      }

      setValue(data.quantity)
      const representation = buildRepresentation(
        data.quantity,
        data.option.value
      )
      onChange(representation)
    },
    [onChange]
  )
  const options = useMemo(() => unitOptions(value), [value])

  return (
    <MultiInput<ISO8601Duration.TimeElement>
      className={durationControlSelectors.container}
      defaultOptionValue={defaultUnit}
      fieldsetName={omitFieldsetName ? undefined : 'Duration'}
      hideFieldsetLegend={hideFieldsetLegend}
      htmlId={htmlId}
      initialOptionValue={initialUnit ?? validatedUnit(defaultUnit)}
      initialQuantity={value}
      maxValue={maxValue}
      minValue={minValue}
      onChange={wrappedOnChange}
      options={options}
      required={required}
    />
  )
}
DurationControl.displayName = 'Satellite: DurationControl'
